"use client";

import React, { useState, useMemo } from "react";
import { CodeHighlight } from "@/components/CodeHighlight";
import {
  Globe,
  Search,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  Sparkles,
  Code2,
  Copy,
  Check,
  ImageOff,
  FileCode,
  Zap,
  Monitor,
  Smartphone,
  LayoutList,
  LayoutGrid,
} from "lucide-react";

interface PreviewState {
  url: string;
  siteName: string;
  title: string;
  description: string;
  imageUrl: string;
  imageRaw: string;
  canonical: string;
  twitterCard: "summary_large_image" | "summary";
}

type PlatformFilter = "all" | "google" | "whatsapp" | "linkedin" | "slack";

const PRESET_OPTIMAL: PreviewState = {
  url: "https://lukiluki.me/garden/qr-code-generator",
  siteName: "Lukas Kopischke",
  title: "QR-Code-Generator ohne Ablaufdatum – Lokal als SVG & PNG",
  description:
    "Statische QR-Codes für WLAN, Links und Visitenkarten direkt im Browser erstellen – ohne Weiterleitungs-Abofalle und komplett offline nutzbar.",
  imageUrl: "/opengraph-image",
  imageRaw: "https://lukiluki.me/opengraph-image",
  canonical: "https://lukiluki.me/garden/qr-code-generator",
  twitterCard: "summary_large_image",
};

const PRESET_BROKEN: PreviewState = {
  url: "https://beispiel-shop.de/aktionen/sommer-kampagne-2026",
  siteName: "beispiel-shop.de",
  title:
    "Willkommen auf unserer neuen Webseite – Hier findest du alle Informationen rund um unsere aktuellen Angebote, Produkte und Neuigkeiten im Überblick",
  description: "Startseite.",
  imageUrl: "",
  imageRaw: "/images/banner.png",
  canonical: "",
  twitterCard: "summary",
};

const SAMPLE_HEAD_HTML = `<head>
  <title>Lokaler Diff-Checker – Verträge & JSON privat vergleichen</title>
  <meta name="description" content="Texte, JSON und Markdown ohne Server-Upload direkt im Browser vergleichen und formatieren." />
  <link rel="canonical" href="https://lukiluki.me/garden/diff-checker" />
  <meta property="og:title" content="Diff-Checker & Smart-Formatter im Browser" />
  <meta property="og:description" content="100 % offline nutzbarer Vergleich für Verträge, JSON und Markdown inklusive Live-Vorschau." />
  <meta property="og:image" content="https://lukiluki.me/opengraph-image" />
  <meta property="og:site_name" content="Lukiluki Digital Garden" />
  <meta name="twitter:card" content="summary_large_image" />
</head>`;

function parseLocalHeadHtml(html: string, fallbackUrl: string): PreviewState {
  const titleMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const htmlTitle = titleMatch ? titleMatch[1].replace(/\s+/g, " ").trim() : "";

  const meta: Record<string, string> = {};
  const metaRegex = /<meta\b([^>]*?)>/gi;
  let m: RegExpExecArray | null;
  while ((m = metaRegex.exec(html)) !== null) {
    const attrs = m[1];
    const prop = attrs.match(/(?:property|name)\s*=\s*["']([^"']+)["']/i)?.[1]?.toLowerCase();
    const content = attrs.match(/content\s*=\s*["']([^"']*)["']/i)?.[1];
    if (prop && content !== undefined && !(prop in meta)) {
      meta[prop] = content.trim();
    }
  }

  const canonical =
    html.match(/<link[^>]*rel=["']canonical["'][^>]*href=["']([^"']+)["']/i)?.[1] ||
    html.match(/<link[^>]*href=["']([^"']+)["'][^>]*rel=["']canonical["']/i)?.[1] ||
    "";

  const rawImg = meta["og:image"] || meta["twitter:image"] || "";
  const isAbsoluteImg = /^https?:\/\//i.test(rawImg);

  let host = "deine-domain.de";
  try {
    host = new URL(canonical || fallbackUrl).hostname.replace(/^www\./, "");
  } catch {
    // ignore invalid URL
  }

  return {
    url: canonical || fallbackUrl,
    siteName: meta["og:site_name"] || host,
    title: meta["og:title"] || meta["twitter:title"] || htmlTitle || "",
    description: meta["og:description"] || meta["twitter:description"] || meta["description"] || "",
    imageUrl: isAbsoluteImg ? rawImg : "",
    imageRaw: rawImg,
    canonical,
    twitterCard: meta["twitter:card"] === "summary" ? "summary" : "summary_large_image",
  };
}

function truncateText(text: string, maxChars: number): string {
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1).trimEnd()}…`;
}

function extractDomain(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, "");
  } catch {
    return "beispiel.de";
  }
}

function formatGoogleBreadcrumb(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    const segments = u.pathname.split("/").filter(Boolean).slice(0, 3);
    if (segments.length === 0) return u.origin;
    return `${u.origin} › ${segments.join(" › ")}`;
  } catch {
    return rawUrl;
  }
}

export default function LinkPreviewPlayground() {
  const [urlInput, setUrlInput] = useState("https://lukiluki.me/garden/geo");
  const [state, setState] = useState<PreviewState>(PRESET_OPTIMAL);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchMeta, setFetchMeta] = useState<{
    ms: number;
    status: number;
    redirected: boolean;
  } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showHtmlPaste, setShowHtmlPaste] = useState(false);
  const [rawHeadInput, setRawHeadInput] = useState(SAMPLE_HEAD_HTML);
  const [showCodeGen, setShowCodeGen] = useState(false);
  const [codeLang, setCodeLang] = useState<"html" | "nextjs">("html");
  const [copiedCode, setCopiedCode] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);

  // View controls for realistic full-size inspection
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [layoutMode, setLayoutMode] = useState<"single" | "grid">("single");
  const [googleDevice, setGoogleDevice] = useState<"desktop" | "mobile">("desktop");

  const fetchSpecificUrl = async (targetUrl: string) => {
    const clean = targetUrl.trim();
    if (!clean) return;
    setUrlInput(clean);
    setIsLoading(true);
    setFetchError(null);
    setImgBroken(false);
    setImgDims(null);

    try {
      const res = await fetch("/api/link-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: clean }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setFetchError(data.error || "URL konnte nicht ausgelesen werden.");
      } else {
        setState({
          url: data.finalUrl || clean,
          siteName: data.siteName || extractDomain(data.finalUrl || clean),
          title: data.title || "",
          description: data.description || "",
          imageUrl: data.image || "",
          imageRaw: data.ogImageRaw || data.twitterImageRaw || data.image || "",
          canonical: data.canonical || "",
          twitterCard: data.twitterCard === "summary" ? "summary" : "summary_large_image",
        });
        setFetchMeta({
          ms: data.responseTimeMs ?? 0,
          status: data.statusCode ?? 200,
          redirected: Boolean(data.redirected),
        });
      }
    } catch {
      setFetchError(
        "Netzwerkfehler oder Offline-Modus aktiv. Nutze rechts den Live-Editor oder füge lokal kopiertes <head>-HTML ein!"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchUrl = async (e?: React.FormEvent) => {
    e?.preventDefault();
    await fetchSpecificUrl(urlInput);
  };

  const diagnostics = useMemo(() => {
    const items: Array<{ level: "ok" | "warn" | "err"; text: string }> = [];

    // 1. Title check
    if (!state.title.trim()) {
      items.push({ level: "err", text: "Kein Titel vorhanden (<title> und og:title fehlen)." });
    } else if (state.title.length > 65) {
      items.push({
        level: "warn",
        text: `Titel ist ${state.title.length} Zeichen lang – Google (~60 Z.) und WhatsApp schneiden das Ende ab.`,
      });
    } else if (state.title.length < 25) {
      items.push({
        level: "warn",
        text: `Titel ist kurz (${state.title.length} Zeichen) – verschenkt Platz in Suchergebnissen.`,
      });
    } else {
      items.push({
        level: "ok",
        text: `Optimale Titellänge (${state.title.length} / 60 Zeichen).`,
      });
    }

    // 2. Description check
    if (!state.description.trim()) {
      items.push({
        level: "err",
        text: "Beschreibung fehlt – Google zieht zufälligen Fließtext, Messenger zeigen oft nur die URL.",
      });
    } else if (state.description.length > 160) {
      items.push({
        level: "warn",
        text: `Beschreibung (${state.description.length} Zeichen) wird ab ~155–160 Zeichen gekürzt.`,
      });
    } else if (state.description.length < 70) {
      items.push({
        level: "warn",
        text: `Beschreibung ist knapp (${state.description.length} Zeichen) – ideal sind 110–155 Zeichen.`,
      });
    } else {
      items.push({
        level: "ok",
        text: `Saubere Beschreibungslänge (${state.description.length} / 155 Zeichen).`,
      });
    }

    // 3. Image URL & Resolution check
    if (!state.imageRaw.trim()) {
      items.push({
        level: "err",
        text: "Kein Vorschaubild (og:image) hinterlegt – Links gehen in Feeds optisch unter.",
      });
    } else if (!/^https?:\/\//i.test(state.imageRaw.trim())) {
      items.push({
        level: "err",
        text: `Relativer Bildpfad („${state.imageRaw}“) – OpenGraph verlangt zwingend https://!`,
      });
    } else if (imgBroken) {
      items.push({
        level: "warn",
        text: "Bild-URL konnte nicht geladen werden (404, CORS/Hotlink-Sperre oder ungültiges Format).",
      });
    } else if (imgDims && (imgDims.w < 600 || imgDims.h < 315)) {
      items.push({
        level: "warn",
        text: `Bildauflösung klein (${imgDims.w}×${imgDims.h} px) – LinkedIn & X stufen Bilder unter 600×315 px oft auf ein Mini-Quadrat herab.`,
      });
    } else if (imgDims) {
      const ratio = (imgDims.w / imgDims.h).toFixed(2);
      items.push({
        level: "ok",
        text: `Vorschaubild geladen: ${imgDims.w} × ${imgDims.h} px (Seitenverhältnis ${ratio}:1, Ziel ~1.91:1).`,
      });
    } else {
      items.push({
        level: "ok",
        text: "Absolute https://-Bildadresse hinterlegt (Zielmaß: 1200×630 px, < 300 KB für WhatsApp).",
      });
    }

    // 4. Twitter Card type
    if (state.twitterCard === "summary") {
      items.push({
        level: "warn",
        text: "twitter:card steht auf „summary“ (kleines Quadrat) statt „summary_large_image“.",
      });
    } else {
      items.push({
        level: "ok",
        text: "twitter:card = summary_large_image aktiviert große Banner-Darstellung.",
      });
    }

    // 5. Canonical check
    if (!state.canonical.trim()) {
      items.push({
        level: "warn",
        text: "Kein <link rel=\"canonical\"> erkannt – UTM-Parameter können geteilte Zähler aufspalten.",
      });
    } else {
      items.push({
        level: "ok",
        text: "Canonical-URL sauber definiert.",
      });
    }

    return items;
  }, [state, imgBroken, imgDims]);

  const okCount = diagnostics.filter((d) => d.level === "ok").length;
  const domain = extractDomain(state.url);
  const hasUsableImage = Boolean(state.imageUrl.trim()) && !imgBroken;

  const generatedCode = useMemo(() => {
    const safeTitle = state.title.replace(/"/g, "&quot;");
    const safeDesc = state.description.replace(/"/g, "&quot;");
    const img = state.imageRaw.startsWith("http")
      ? state.imageRaw
      : `https://${domain}${state.imageRaw.startsWith("/") ? "" : "/"}${state.imageRaw || "og-image.png"}`;

    if (codeLang === "html") {
      return `<!-- Primäre Suchmaschinen-Tags -->
<title>${safeTitle}</title>
<meta name="description" content="${safeDesc}" />
<link rel="canonical" href="${state.url}" />

<!-- OpenGraph (WhatsApp, LinkedIn, Slack, Discord) -->
<meta property="og:type" content="website" />
<meta property="og:url" content="${state.url}" />
<meta property="og:site_name" content="${state.siteName}" />
<meta property="og:title" content="${safeTitle}" />
<meta property="og:description" content="${safeDesc}" />
<meta property="og:image" content="${img}" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />

<!-- Social Card Größe -->
<meta name="twitter:card" content="${state.twitterCard}" />
<meta name="twitter:title" content="${safeTitle}" />
<meta name="twitter:description" content="${safeDesc}" />
<meta name="twitter:image" content="${img}" />`;
    }

    return `import type { Metadata } from "next";

export const metadata: Metadata = {
  title: ${JSON.stringify(state.title)},
  description: ${JSON.stringify(state.description)},
  alternates: {
    canonical: ${JSON.stringify(state.url)},
  },
  openGraph: {
    title: ${JSON.stringify(state.title)},
    description: ${JSON.stringify(state.description)},
    url: ${JSON.stringify(state.url)},
    siteName: ${JSON.stringify(state.siteName)},
    type: "website",
    images: [
      {
        url: ${JSON.stringify(img)},
        width: 1200,
        height: 630,
      },
    ],
  },
  twitter: {
    card: ${JSON.stringify(state.twitterCard)},
    title: ${JSON.stringify(state.title)},
    description: ${JSON.stringify(state.description)},
    images: [${JSON.stringify(img)}],
  },
};`;
  }, [state, codeLang, domain]);

  const renderPreviewImage = (aspectClass: string, badgeLabel?: string) => {
    if (!hasUsableImage) {
      return (
        <div
          className={`flex ${aspectClass} w-full flex-col items-center justify-center gap-2 bg-neutral-100 px-6 text-center text-xs text-neutral-500 dark:bg-neutral-800/80 dark:text-neutral-400`}
        >
          <ImageOff className="h-7 w-7 text-neutral-400" />
          <span className="font-medium">
            {state.imageRaw && !/^https?:\/\//i.test(state.imageRaw)
              ? `Relativer Pfad wird von Crawlern ignoriert (${state.imageRaw})`
              : imgBroken
                ? "Bild-URL konnte nicht geladen werden (404 / Blockiert)"
                : "Kein og:image gesetzt – Vorschau erscheint ohne Bild"}
          </span>
        </div>
      );
    }

    return (
      <div className={`relative ${aspectClass} w-full overflow-hidden bg-neutral-200 dark:bg-neutral-800`}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={state.imageUrl}
          alt={state.title || "OpenGraph Vorschaubild"}
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth;
            const h = e.currentTarget.naturalHeight;
            if (w && h) setImgDims({ w, h });
          }}
          onError={() => setImgBroken(true)}
          className="h-full w-full object-cover"
        />
        {badgeLabel && (
          <span className="absolute bottom-2.5 right-2.5 rounded-md bg-black/70 px-2 py-0.5 font-mono text-[11px] font-medium text-white backdrop-blur-xs">
            {badgeLabel}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 1. PROMINENT FULL-WIDTH URL FETCH BAR */}
      <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/80 p-3.5 sm:p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
        <form onSubmit={handleFetchUrl} className="flex flex-col gap-2.5 sm:flex-row">
          <div className="relative flex-1">
            <Globe className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              aria-label="Zu prüfende Webseiten-URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://deine-domain.de/artikel-oder-produkt"
              className="min-h-[48px] w-full rounded-xl border border-neutral-300 bg-white pl-11 pr-4 font-mono text-sm sm:text-base text-neutral-900 shadow-2xs focus:border-orange-500 focus:outline-hidden dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-600 px-6 py-2.5 text-sm font-bold text-white shadow-xs transition-colors hover:bg-orange-700 disabled:opacity-60 cursor-pointer"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Lese &lt;head&gt;…</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                <span>URL live prüfen</span>
              </>
            )}
          </button>
        </form>

        {/* Quick-Test URLs, Offline HTML Toggle & Telemetry */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-neutral-500 dark:text-neutral-400 mr-1">
              Schnelltest:
            </span>
            <button
              type="button"
              onClick={() => fetchSpecificUrl("https://lukiluki.me/garden/geo")}
              className="rounded-lg border border-neutral-200 bg-white px-2.5 py-1 font-mono text-[11px] font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
            >
              lukiluki.me/garden/geo
            </button>
            <button
              type="button"
              onClick={() => {
                setImgBroken(false);
                setImgDims(null);
                setFetchError(null);
                setState(PRESET_OPTIMAL);
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
            >
              <Sparkles className="h-3 w-3 text-emerald-500" />
              Optimal-Vorlage
            </button>
            <button
              type="button"
              onClick={() => {
                setImgBroken(false);
                setImgDims(null);
                setFetchError(null);
                setState(PRESET_BROKEN);
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
            >
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Typische Fehler-URL
            </button>
            <button
              type="button"
              onClick={() => setShowHtmlPaste((p) => !p)}
              className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-medium cursor-pointer ${
                showHtmlPaste
                  ? "border-orange-500/40 bg-orange-500/10 text-orange-800 dark:text-orange-300"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
              }`}
            >
              <FileCode className="h-3.5 w-3.5" />
              <span>&lt;head&gt;-HTML einfügen (Offline / Localhost)</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
            {fetchMeta && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-800 dark:text-emerald-300">
                <Zap className="h-3 w-3" />
                HTTP {fetchMeta.status} · {fetchMeta.ms} ms
              </span>
            )}
            {imgDims && (
              <span className="rounded-md bg-neutral-200/80 px-2 py-0.5 font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                Bild: {imgDims.w}×{imgDims.h} px
              </span>
            )}
            <span
              className={`rounded-md px-2 py-0.5 font-semibold ${
                okCount === diagnostics.length
                  ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                  : "bg-amber-500/15 text-amber-800 dark:text-amber-300"
              }`}
            >
              Checks: {okCount}/{diagnostics.length} OK
            </span>
          </div>
        </div>

        {fetchError && (
          <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-900 dark:text-amber-200">
            {fetchError}
          </div>
        )}

        {showHtmlPaste && (
          <div className="mt-3 space-y-2 rounded-xl border border-neutral-200 bg-white p-3.5 dark:border-neutral-800 dark:bg-neutral-900">
            <label
              htmlFor="lp-raw-head"
              className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
            >
              Lokales &lt;head&gt;-HTML für Localhost- oder Offline-Prüfung einfügen:
            </label>
            <textarea
              id="lp-raw-head"
              rows={5}
              value={rawHeadInput}
              onChange={(e) => setRawHeadInput(e.target.value)}
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 font-mono text-xs text-neutral-900 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            />
            <button
              type="button"
              onClick={() => {
                setImgBroken(false);
                setImgDims(null);
                setState(parseLocalHeadHtml(rawHeadInput, state.url));
              }}
              className="inline-flex min-h-[38px] items-center gap-1.5 rounded-lg bg-neutral-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 cursor-pointer"
            >
              HTML-Tags jetzt auswerten
            </button>
          </div>
        )}
      </div>

      {/* 2. MAIN WORKSPACE: LARGE REAL-SCALE PREVIEWS (LEFT 8 COLS) + LIVE EDITOR & DIAGNOSTICS (RIGHT 4 COLS) */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12 items-start">
        {/* LEFT 8 COLUMNS: FULL-SIZE REALISTIC PLATFORM PREVIEWS */}
        <div className="flex flex-col gap-5 xl:col-span-8">
          {/* Platform Filter + Scale Mode Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-200/80 bg-neutral-100/70 p-1.5 dark:border-neutral-800 dark:bg-neutral-900/70">
            <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Plattform-Filter">
              {(
                [
                  { id: "all", label: "Alle 4 Plattformen" },
                  { id: "google", label: "Google Suche" },
                  { id: "whatsapp", label: "WhatsApp" },
                  { id: "linkedin", label: "LinkedIn" },
                  { id: "slack", label: "Slack / Discord" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={platformFilter === tab.id}
                  onClick={() => setPlatformFilter(tab.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                    platformFilter === tab.id
                      ? "bg-white text-neutral-900 shadow-2xs dark:bg-neutral-800 dark:text-white"
                      : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {platformFilter === "all" && (
              <div className="flex items-center gap-1 rounded-lg bg-white/80 p-0.5 dark:bg-neutral-950/60">
                <button
                  type="button"
                  onClick={() => setLayoutMode("single")}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold cursor-pointer ${
                    layoutMode === "single"
                      ? "bg-orange-600 text-white"
                      : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400"
                  }`}
                  title="Jede Karte in echter 1:1-Desktop-Breite untereinander anzeigen"
                >
                  <LayoutList className="h-3.5 w-3.5" />
                  <span>1:1 Originalgröße</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLayoutMode("grid")}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold cursor-pointer ${
                    layoutMode === "grid"
                      ? "bg-orange-600 text-white"
                      : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400"
                  }`}
                  title="2 Karten nebeneinander vergleichen"
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  <span>2-Spalten</span>
                </button>
              </div>
            )}
          </div>

          {/* PREVIEW CARDS CANVAS */}
          <div
            className={
              platformFilter === "all" && layoutMode === "grid"
                ? "grid grid-cols-1 gap-5 2xl:grid-cols-2"
                : "flex flex-col gap-6"
            }
          >
            {/* CARD 1: GOOGLE SEARCH (REAL 600px DESKTOP / 380px MOBILE PROPORTIONS) */}
            {(platformFilter === "all" || platformFilter === "google") && (
              <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/60 p-4 sm:p-6 dark:border-neutral-800 dark:bg-neutral-950/50">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">
                      1 · Google Suchergebnis (SERP)
                    </span>
                    <span className="font-mono text-[11px] text-neutral-500">
                      Titel ~60 Z. · Snippet ~155 Z.
                    </span>
                  </div>
                  <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5 dark:border-neutral-800 dark:bg-neutral-900">
                    <button
                      type="button"
                      onClick={() => setGoogleDevice("desktop")}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium cursor-pointer ${
                        googleDevice === "desktop"
                          ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                          : "text-neutral-500"
                      }`}
                    >
                      <Monitor className="h-3 w-3" />
                      Desktop (600 px)
                    </button>
                    <button
                      type="button"
                      onClick={() => setGoogleDevice("mobile")}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium cursor-pointer ${
                        googleDevice === "mobile"
                          ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                          : "text-neutral-500"
                      }`}
                    >
                      <Smartphone className="h-3 w-3" />
                      Mobil
                    </button>
                  </div>
                </div>

                {/* Authentic Google Surface */}
                <div
                  className={`rounded-xl border border-neutral-200/90 bg-white p-5 sm:p-6 shadow-xs dark:border-neutral-800 dark:bg-[#1f1f1f] ${
                    googleDevice === "mobile" ? "max-w-[400px]" : "max-w-[652px]"
                  }`}
                >
                  {/* Site attribution row */}
                  <div className="mb-2 flex items-center gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100 text-xs font-bold text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                      {(state.siteName || domain).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 leading-tight">
                      <div className="truncate text-[14px] font-normal text-[#202124] dark:text-[#dadce0]">
                        {state.siteName || domain}
                      </div>
                      <div className="truncate text-[12px] text-[#4d5156] dark:text-[#bdc1c6]">
                        {formatGoogleBreadcrumb(state.url)}
                      </div>
                    </div>
                  </div>

                  {/* Clickable-looking 20px Google Headline */}
                  <div className="mb-1.5 font-sans text-[20px] font-normal leading-[1.3] text-[#1a0dab] hover:underline dark:text-[#8ab4f8]">
                    {truncateText(
                      state.title || "Unbenannte Seite (Kein <title> vorhanden)",
                      googleDevice === "mobile" ? 55 : 60
                    )}
                  </div>

                  {/* Description + optional mobile thumbnail */}
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-sans text-[14px] leading-[1.58] text-[#4d5156] dark:text-[#bdc1c6]">
                      <span className="text-[#70757a] dark:text-[#9aa0a6]">26.10.2026 — </span>
                      {truncateText(
                        state.description ||
                          "Keine Meta-Description hinterlegt – Google extrahiert hier automatisch einen beliebigen Textausschnitt aus der Seite.",
                        googleDevice === "mobile" ? 120 : 158
                      )}
                    </p>
                    {googleDevice === "mobile" && hasUsableImage && (
                      <div className="h-[88px] w-[88px] shrink-0 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-700">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={state.imageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CARD 2: WHATSAPP CHAT BUBBLE (REALISTIC FULL CHAT SIZE) */}
            {(platformFilter === "all" || platformFilter === "whatsapp") && (
              <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/60 p-4 sm:p-6 dark:border-neutral-800 dark:bg-neutral-950/50">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">
                    2 · WhatsApp Messenger-Vorschau
                  </span>
                  <span className="font-mono text-[11px] text-neutral-500">
                    og:image ideal &lt; 300 KB · 1.91:1
                  </span>
                </div>

                {/* WhatsApp Chat Wallpaper Container */}
                <div className="rounded-2xl border border-neutral-200/80 bg-[#efeae2] p-4 sm:p-6 dark:border-neutral-800 dark:bg-[#0b141a]">
                  <div className="ml-auto max-w-[460px] rounded-2xl rounded-tr-xs bg-[#d9fdd3] p-2 shadow-sm dark:bg-[#005c4b]">
                    {/* Inner Link Preview Box */}
                    <div className="overflow-hidden rounded-xl bg-black/[0.045] dark:bg-black/25">
                      {state.twitterCard === "summary" ? (
                        <div className="flex items-stretch">
                          {hasUsableImage && (
                            <div className="w-28 shrink-0 overflow-hidden bg-neutral-200 dark:bg-neutral-800">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={state.imageUrl}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-3">
                            <div className="line-clamp-2 text-[14.5px] font-semibold leading-snug text-neutral-900 dark:text-neutral-100">
                              {truncateText(state.title || domain, 68)}
                            </div>
                            <div className="mt-1 line-clamp-2 text-[13px] leading-snug text-neutral-600 dark:text-neutral-300">
                              {truncateText(state.description, 105)}
                            </div>
                            <div className="mt-1.5 text-[12px] text-neutral-500 dark:text-neutral-400">
                              {domain}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {renderPreviewImage("aspect-[1.91/1]")}
                          <div className="p-3">
                            <div className="line-clamp-2 text-[15px] font-semibold leading-snug text-neutral-900 dark:text-neutral-100">
                              {truncateText(state.title || domain, 72)}
                            </div>
                            {state.description && (
                              <div className="mt-1 line-clamp-2 text-[13px] leading-snug text-neutral-600 dark:text-neutral-300">
                                {truncateText(state.description, 115)}
                              </div>
                            )}
                            <div className="mt-1.5 text-[12px] text-neutral-500 dark:text-neutral-400">
                              {domain}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Message Text + Timestamp */}
                    <div className="mt-2 flex items-end justify-between gap-3 px-1.5 pb-0.5">
                      <span className="truncate text-[14.5px] text-[#027eb5] underline dark:text-[#53bdeb]">
                        {state.url}
                      </span>
                      <span className="shrink-0 text-[11px] text-neutral-500 dark:text-emerald-200/75">
                        22:15 ✓✓
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CARD 3: LINKEDIN FEED POST (ORIGINAL 552px FEED SCALE) */}
            {(platformFilter === "all" || platformFilter === "linkedin") && (
              <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/60 p-4 sm:p-6 dark:border-neutral-800 dark:bg-neutral-950/50">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">
                    3 · LinkedIn Feed-Karte (552 px Originalmaß)
                  </span>
                  <span className="font-mono text-[11px] text-neutral-500">
                    Empfohlen: 1200 × 627 px (1.91:1)
                  </span>
                </div>

                <div className="max-w-[560px] overflow-hidden rounded-xl border border-neutral-300/90 bg-white shadow-xs dark:border-neutral-700 dark:bg-[#1b1f23]">
                  {/* Simulated Author Header */}
                  <div className="flex items-center gap-3 px-4 pt-3.5 pb-2.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      LK
                    </div>
                    <div className="min-w-0 leading-tight">
                      <div className="text-[14px] font-semibold text-neutral-900 dark:text-white">
                        Lukas Kopischke
                      </div>
                      <div className="truncate text-[12px] text-neutral-500 dark:text-neutral-400">
                        Lead Software Engineer · Gerade eben · 🌐
                      </div>
                    </div>
                  </div>

                  <div className="px-4 pb-3 text-[14px] text-neutral-800 dark:text-neutral-200">
                    Kurzer Blick auf die Live-Vorschau für unseren neuen Link:
                  </div>

                  {/* LinkedIn Link Card */}
                  <div className="border-t border-neutral-200 dark:border-neutral-700">
                    {renderPreviewImage(
                      "aspect-[1.91/1]",
                      imgDims ? `${imgDims.w}×${imgDims.h}` : undefined
                    )}
                    <div className="bg-[#f3f6f8] px-4 py-3 dark:bg-[#293138]">
                      <div className="line-clamp-2 text-[15.5px] font-semibold leading-snug text-neutral-900 dark:text-white">
                        {truncateText(state.title || "Kein og:title gefunden", 85)}
                      </div>
                      <div className="mt-1 text-[12.5px] text-neutral-500 dark:text-neutral-400">
                        {domain}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CARD 4: SLACK / DISCORD RICH EMBED (540px SCALE) */}
            {(platformFilter === "all" || platformFilter === "slack") && (
              <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/60 p-4 sm:p-6 dark:border-neutral-800 dark:bg-neutral-950/50">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">
                    4 · Slack & Discord Unfurl
                  </span>
                  <span className="font-mono text-[11px] text-neutral-500">
                    og:site_name + og:title + og:description
                  </span>
                </div>

                <div className="max-w-[560px] rounded-xl border border-neutral-200/90 bg-white p-4 sm:p-5 shadow-xs dark:border-neutral-800 dark:bg-[#222529]">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <div className="mb-1 flex items-center gap-2 text-[13px] font-bold text-neutral-800 dark:text-neutral-200">
                      <span className="flex h-4 w-4 items-center justify-center rounded-xs bg-neutral-200 text-[10px] font-bold dark:bg-neutral-700">
                        {(state.siteName || domain).slice(0, 1).toUpperCase()}
                      </span>
                      <span>{state.siteName || domain}</span>
                    </div>

                    <div className="text-[15.5px] font-bold leading-snug text-[#1264a3] hover:underline dark:text-[#1d9bd1]">
                      {truncateText(state.title || state.url, 90)}
                    </div>

                    {state.description && (
                      <p className="mt-1.5 text-[14px] leading-relaxed text-neutral-700 dark:text-neutral-300">
                        {truncateText(state.description, 175)}
                      </p>
                    )}

                    <div className="mt-3 max-w-[460px] overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
                      {renderPreviewImage("aspect-[1.91/1]")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT 4 COLUMNS: STICKY LIVE META EDITOR + DIAGNOSTICS + CODE EXPORT */}
        <div className="flex flex-col gap-4 xl:col-span-4 xl:sticky xl:top-24">
          {/* Live Meta Fields Editor */}
          <div className="rounded-2xl border border-neutral-200/90 bg-neutral-50/70 p-4 sm:p-5 dark:border-neutral-800 dark:bg-neutral-950/60 space-y-3.5">
            <div className="flex items-center justify-between">
              <div className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                Meta-Tags live bearbeiten
              </div>
              <span className="text-[11px] text-neutral-500">Echtzeit-Vorschau</span>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <label htmlFor="lp-title" className="font-semibold text-neutral-700 dark:text-neutral-300">
                  Titel (og:title / &lt;title&gt;)
                </label>
                <span
                  className={`font-mono font-semibold ${
                    state.title.length > 65 || state.title.length === 0
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {state.title.length} / 60
                </span>
              </div>
              <input
                id="lp-title"
                type="text"
                value={state.title}
                onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
                className="min-h-[40px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs sm:text-sm text-neutral-900 focus:border-orange-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <label htmlFor="lp-desc" className="font-semibold text-neutral-700 dark:text-neutral-300">
                  Beschreibung (og:description)
                </label>
                <span
                  className={`font-mono font-semibold ${
                    state.description.length > 160 || state.description.length < 50
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {state.description.length} / 155
                </span>
              </div>
              <textarea
                id="lp-desc"
                rows={3}
                value={state.description}
                onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
                className="w-full rounded-xl border border-neutral-200 bg-white p-3 text-xs sm:text-sm text-neutral-900 focus:border-orange-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
              />
            </div>

            <div>
              <label
                htmlFor="lp-img"
                className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
              >
                Vorschaubild-URL (og:image)
              </label>
              <input
                id="lp-img"
                type="text"
                value={state.imageRaw}
                onChange={(e) => {
                  const val = e.target.value;
                  setImgBroken(false);
                  setImgDims(null);
                  setState((s) => ({
                    ...s,
                    imageRaw: val,
                    imageUrl: /^https?:\/\//i.test(val.trim()) || val.startsWith("/") ? val.trim() : "",
                  }));
                }}
                placeholder="https://deine-domain.de/og-image.png"
                className="min-h-[40px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 font-mono text-xs text-neutral-900 focus:border-orange-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
              />
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label
                  htmlFor="lp-site"
                  className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                >
                  og:site_name
                </label>
                <input
                  id="lp-site"
                  type="text"
                  value={state.siteName}
                  onChange={(e) => setState((s) => ({ ...s, siteName: e.target.value }))}
                  className="min-h-[38px] w-full rounded-xl border border-neutral-200 bg-white px-2.5 py-1.5 text-xs dark:border-neutral-800 dark:bg-neutral-900"
                />
              </div>
              <div>
                <label
                  htmlFor="lp-card"
                  className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                >
                  Kartenformat
                </label>
                <select
                  id="lp-card"
                  value={state.twitterCard}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      twitterCard: e.target.value as "summary_large_image" | "summary",
                    }))
                  }
                  className="min-h-[38px] w-full rounded-xl border border-neutral-200 bg-white px-2 py-1.5 text-xs dark:border-neutral-800 dark:bg-neutral-900"
                >
                  <option value="summary_large_image">Großes Banner (1.91:1)</option>
                  <option value="summary">Kleines Quadrat (1:1)</option>
                </select>
              </div>
            </div>
          </div>

          {/* 5-Point Diagnostics Card */}
          <div className="rounded-2xl border border-neutral-200/90 bg-white p-4 sm:p-5 dark:border-neutral-800 dark:bg-neutral-950/60 space-y-2.5">
            <div className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
              Automatische Fehler-Diagnose
            </div>
            <div className="space-y-2">
              {diagnostics.map((d, i) => (
                <div key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                  {d.level === "ok" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  ) : d.level === "warn" ? (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                  )}
                  <span className="text-neutral-700 dark:text-neutral-300">{d.text}</span>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowCodeGen((p) => !p)}
                className="inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-2 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 cursor-pointer"
              >
                <Code2 className="h-4 w-4" />
                <span>
                  {showCodeGen
                    ? "Meta-Tag-Generator ausblenden"
                    : "Fertigen HTML- / Next.js-Code generieren"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. EXPANDABLE READY-TO-COPY CODE GENERATOR */}
      {showCodeGen && (
        <div className="overflow-hidden rounded-2xl border border-neutral-800 bg-zinc-950 shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-900 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCodeLang("html")}
                className={`rounded-lg px-2.5 py-1 font-mono text-xs font-semibold cursor-pointer ${
                  codeLang === "html"
                    ? "bg-orange-600 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                HTML &lt;head&gt;
              </button>
              <button
                type="button"
                onClick={() => setCodeLang("nextjs")}
                className={`rounded-lg px-2.5 py-1 font-mono text-xs font-semibold cursor-pointer ${
                  codeLang === "nextjs"
                    ? "bg-orange-600 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                Next.js Metadata
              </button>
            </div>

            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard?.writeText(generatedCode);
                setCopiedCode(true);
                setTimeout(() => setCopiedCode(false), 1800);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs font-medium text-zinc-200 hover:bg-zinc-700 cursor-pointer"
            >
              {copiedCode ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                  <span>Kopiert!</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span>Code kopieren</span>
                </>
              )}
            </button>
          </div>
          <div className="p-4 overflow-x-auto">
            <CodeHighlight
              code={generatedCode}
              language={codeLang === "html" ? "markup" : "typescript"}
              showLineNumbers
            />
          </div>
        </div>
      )}
    </div>
  );
}
